import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBarcode, faCheckCircle, faExclamationTriangle, faTimesCircle,
    faRotateLeft, faCheck, faRotate, faXmark, faCircleCheck,
    faTriangleExclamation, faHandPointer, faChevronLeft, faChevronRight,
    faBoxOpen
} from '@fortawesome/free-solid-svg-icons';
import './OrderItemsPopup.css';

function OrderItemsPopup({ orderNumber, trackingNumber, orderRows, onClose, onNavigate, orderDates, onAllComplete, onStatusChange, onAmazonShip }) {
    const [assemblyItems, setAssemblyItems]   = useState([]);
    const [assemblyStatus, setAssemblyStatus] = useState('pending');
    const [assemblyLoading, setAssemblyLoading] = useState(false);

    const [gtinInput, setGtinInput] = useState('');
    const [scanFeedback, setScanFeedback] = useState(null); // { type, message }
    const [scanning, setScanning]         = useState(false);
    const [inputFlash, setInputFlash]     = useState(''); // 'flash-ok' | 'flash-err'
    const [resyncing, setResyncing]       = useState(false);

    const gtinRef       = useRef(null);
    const feedbackTimer = useRef(null);

    // Текущий индекс в списке строк (по паре order+tracking)
    const currentKey = orderNumber + '|' + (trackingNumber || '');
    const currentIndex = orderRows
        ? orderRows.findIndex(r => r.order_number + '|' + (r.tracking_number || '') === currentKey)
        : -1;
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex !== -1 && orderRows && currentIndex < orderRows.length - 1;

    // ── Load assembly ─────────────────────────────────────────
    const fetchAssembly = useCallback((num, tracking) => {
        setAssemblyLoading(true);
        const trackingParam = tracking ? `?tracking=${encodeURIComponent(tracking)}` : '';
        api.get(`/api/assembly/${encodeURIComponent(num)}${trackingParam}`)
            .then(res => {
                setAssemblyItems(res.data.items || []);
                setAssemblyStatus(res.data.assembly_status || 'pending');
            })
            .catch(err => console.error('Assembly fetch error:', err))
            .finally(() => setAssemblyLoading(false));
    }, []);

    useEffect(() => {
        if (!orderNumber) return;
        fetchAssembly(orderNumber, trackingNumber);
    }, [orderNumber, trackingNumber, fetchAssembly]);

    useEffect(() => {
        if (!assemblyLoading && gtinRef.current) {
            setTimeout(() => gtinRef.current?.focus(), 50);
        }
    }, [assemblyLoading]);

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') { onClose(); return; }
            if (document.activeElement === gtinRef.current) return;
            if (e.key === 'ArrowLeft'  && hasPrev) onNavigate({ orderNumber: orderRows[currentIndex - 1].order_number, trackingNumber: orderRows[currentIndex - 1].tracking_number });
            if (e.key === 'ArrowRight' && hasNext) onNavigate({ orderNumber: orderRows[currentIndex + 1].order_number, trackingNumber: orderRows[currentIndex + 1].tracking_number });
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose, onNavigate, hasPrev, hasNext, currentIndex, orderRows]);

    // ── Feedback ──────────────────────────────────────────────
    const showFeedback = (type, message) => {
        setScanFeedback({ type, message });
        setInputFlash(type === 'ok' ? 'flash-ok' : 'flash-err');
        clearTimeout(feedbackTimer.current);
        feedbackTimer.current = setTimeout(() => {
            setScanFeedback(null);
            setInputFlash('');
        }, 3500);
    };

    // ── GTIN scan ─────────────────────────────────────────────
    const handleGtinScan = async () => {
        const gtin = gtinInput.trim();
        if (!gtin || scanning) return;
        setScanning(true);
        setGtinInput('');

        try {
            const res  = await api.post('/api/assembly/scan', {
                order_number: orderNumber,
                gtin,
                tracking_number: trackingNumber || ''
            });
            const data = res.data;

            if (data.match === 'ok') {
                setAssemblyItems(prev => prev.map(item =>
                    item.sku === data.sku ? { ...item, quantity_scanned: data.quantity_scanned } : item
                ));
                const ssWarn = data.ss_stock_qty === 0 && !data.auto_transferred
                    ? ` SS Stock for ${data.sku} is now empty — please replenish SS Stock.`
                    : (data.auto_transferred ? ' Taken from inventory.' : '');
                const fbType = ssWarn && !data.auto_transferred ? 'warn' : 'ok';
                if (data.all_complete) {
                    await handleComplete();
                    showFeedback(fbType, `All items scanned — assembly marked complete!${ssWarn}`);
                    if (onAllComplete) setTimeout(onAllComplete, ssWarn ? 2500 : 700);
                } else if (data.item_complete) {
                    onStatusChange?.('in_progress');
                    showFeedback(fbType, `${data.sku} complete (${data.quantity_scanned}/${data.quantity_required})${ssWarn}`);
                } else {
                    onStatusChange?.('in_progress');
                    showFeedback(fbType, `${data.sku} — ${data.quantity_scanned} of ${data.quantity_required} scanned${ssWarn}`);
                }
            } else if (data.match === 'ss_stock_empty') {
                showFeedback('error', `SS Stock for ${data.sku} is empty. Scan not counted — replenish SS Stock first.`);
            } else if (data.match === 'already_complete') {
                showFeedback('warn', `Already fully scanned: ${data.sku}`);
            } else if (data.match === 'wrong_order') {
                showFeedback('error', `Not in this order: ${data.sku}`);
            } else {
                showFeedback('error', data.message || 'GTIN not found in catalog');
            }
        } catch (err) {
            showFeedback('error', err.response?.data?.error || 'Network error');
        } finally {
            setScanning(false);
            setTimeout(() => gtinRef.current?.focus(), 50);
        }
    };

    // ── Undo scan ─────────────────────────────────────────────
    const handleUndoScan = async (sku) => {
        try {
            await api.delete('/api/assembly/scan', {
                data: { order_number: orderNumber, sku, tracking_number: trackingNumber || '' }
            });
            setAssemblyItems(prev => prev.map(item =>
                item.sku === sku ? { ...item, quantity_scanned: Math.max(0, item.quantity_scanned - 1) } : item
            ));
        } catch (err) {
            console.error('Undo scan error:', err);
        }
    };

    // ── Manual mark done (for no-GTIN items) ─────────────────
    const handleManualScan = async (sku, gtin) => {
        try {
            const res  = await api.post('/api/assembly/scan', {
                order_number: orderNumber,
                gtin: gtin || `MANUAL_${sku}`,
                tracking_number: trackingNumber || ''
            });
            const data = res.data;
            if (data.match === 'ok') {
                setAssemblyItems(prev => prev.map(item =>
                    item.sku === data.sku ? { ...item, quantity_scanned: data.quantity_scanned } : item
                ));
                if (data.all_complete) {
                    await handleComplete();
                    if (onAllComplete) setTimeout(onAllComplete, 700);
                } else {
                    onStatusChange?.('in_progress');
                }
            }
        } catch (err) {
            console.error('Manual scan error:', err);
        }
    };

    // ── Mark item as sent via Amazon ─────────────────────────
    const handleAmazonShip = async (sku) => {
        try {
            const res = await api.post('/api/assembly/amazon-ship', {
                order_number: orderNumber,
                sku,
                tracking_number: trackingNumber || ''
            });
            const data = res.data;
            if (data.success || data.match === 'already_complete') {
                setAssemblyItems(prev => prev.map(item =>
                    item.sku === sku ? { ...item, quantity_scanned: data.quantity_scanned, is_amazon: true } : item
                ));
                if (data.all_complete) {
                    await handleComplete();
                    if (onAllComplete) setTimeout(onAllComplete, 700);
                } else {
                    onStatusChange?.('in_progress');
                }
                onAmazonShip?.(orderNumber);
            }
        } catch (err) {
            console.error('Amazon ship error:', err);
        }
    };

    // ── Re-sync items from Veeqo ─────────────────────────────
    const handleResyncVeeqo = async () => {
        setResyncing(true);
        try {
            await api.get(`/api/veeqo/order/${encodeURIComponent(orderNumber)}?refresh=true`);
            await fetchAssembly(orderNumber, trackingNumber);
        } catch (err) {
            console.error('Veeqo resync error:', err);
        } finally {
            setResyncing(false);
        }
    };

    // ── Complete ──────────────────────────────────────────────
    const handleComplete = async () => {
        try {
            await api.post('/api/assembly/complete', {
                order_number: orderNumber,
                tracking_number: trackingNumber || ''
            });
            setAssemblyStatus('complete');
            onStatusChange?.('complete');
        } catch (err) {
            console.error('Complete error:', err);
        }
    };

    // ── Computed ──────────────────────────────────────────────
    const totalRequired = assemblyItems.reduce((s, i) => s + (i.quantity || 0), 0);
    const totalScanned  = assemblyItems.reduce((s, i) => s + (i.quantity_scanned || 0), 0);
    const allComplete   = assemblyItems.length > 0 && assemblyItems.every(i => i.quantity_scanned >= i.quantity);
    const pct           = totalRequired > 0 ? Math.round(totalScanned / totalRequired * 100) : 0;

    const itemStatus = (item) => {
        if (item.quantity_scanned >= item.quantity) return 'done';
        if (item.quantity_scanned > 0) return 'partial';
        return 'pending';
    };

    const statusLabel = {
        pending:     { text: 'Not started', cls: 'oip-asm-badge--pending' },
        in_progress: { text: 'In progress', cls: 'oip-asm-badge--progress' },
        complete:    { text: 'Complete',    cls: 'oip-asm-badge--done' },
    }[assemblyStatus] || { text: assemblyStatus, cls: '' };

    const feedbackIcon = {
        ok:    faCheckCircle,
        warn:  faExclamationTriangle,
        error: faTimesCircle,
    };

    // Проверяем: split-shipment (есть tracking на items) или обычный заказ
    const isSplitShipment = trackingNumber && assemblyItems.some(i => i.tracking_number);

    return (
        <div className="oip-overlay" onClick={onClose}>
            <div className="oip-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="oip-header">
                    <div className="oip-nav">
                        {orderRows && (
                            <button className="oip-nav-btn"
                                onClick={() => hasPrev && onNavigate({ orderNumber: orderRows[currentIndex - 1].order_number, trackingNumber: orderRows[currentIndex - 1].tracking_number })}
                                disabled={!hasPrev} title="Previous (←)">
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                        )}
                        <div className="oip-title">
                            <span className="oip-title-order">
                                Order <span className="oip-order-num">{orderNumber}</span>
                            </span>
                            {trackingNumber && (
                                <span className="oip-title-tracking">
                                    <FontAwesomeIcon icon={faBoxOpen} className="oip-tracking-icon" />
                                    <span className="oip-tracking-num">{trackingNumber}</span>
                                </span>
                            )}
                            {orderRows && currentIndex !== -1 && (
                                <span className="oip-counter">{currentIndex + 1} / {orderRows.length}</span>
                            )}
                            <span className={`oip-asm-badge ${statusLabel.cls}`}>{statusLabel.text}</span>
                        </div>
                        {orderRows && (
                            <button className="oip-nav-btn"
                                onClick={() => hasNext && onNavigate({ orderNumber: orderRows[currentIndex + 1].order_number, trackingNumber: orderRows[currentIndex + 1].tracking_number })}
                                disabled={!hasNext} title="Next (→)">
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        )}
                    </div>
                    <div className="oip-header-right">
                        <button className="oip-icon-btn" onClick={() => fetchAssembly(orderNumber, trackingNumber)} title="Refresh assembly" disabled={assemblyLoading || resyncing}>
                            <FontAwesomeIcon icon={faRotate} spin={assemblyLoading} />
                        </button>
                        <button className="oip-icon-btn oip-icon-btn--resync" onClick={handleResyncVeeqo} title="Re-sync items from Veeqo" disabled={resyncing || assemblyLoading}>
                            <FontAwesomeIcon icon={faBoxOpen} spin={resyncing} />
                        </button>
                        <button className="oip-icon-btn oip-icon-btn--close" onClick={onClose} title="Close">
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </div>
                </div>

                <div className="oip-body">
                    <div className="oip-scan">

                        {/* Progress bar */}
                        {assemblyItems.length > 0 && (
                            <div className="oip-progress-wrap">
                                <div className="oip-progress-bar">
                                    <div
                                        className={`oip-progress-fill ${allComplete ? 'oip-progress-fill--done' : ''}`}
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                                <span className="oip-progress-label">
                                    {totalScanned} / {totalRequired}
                                    <span className="oip-progress-pct"> · {pct}%</span>
                                </span>
                            </div>
                        )}

                        {/* Assembly complete */}
                        {assemblyStatus === 'complete' && (
                            <div className="oip-complete-done">
                                <FontAwesomeIcon icon={faCircleCheck} />
                                Assembly complete
                            </div>
                        )}

                        {/* GTIN input */}
                        {assemblyStatus !== 'complete' && (
                            <div className="oip-gtin-row">
                                <div className="oip-gtin-input-wrap">
                                    <FontAwesomeIcon icon={faBarcode} className="oip-gtin-icon" />
                                    <input
                                        ref={gtinRef}
                                        className={`oip-gtin-input ${inputFlash}`}
                                        type="text"
                                        value={gtinInput}
                                        onChange={e => setGtinInput(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleGtinScan()}
                                        placeholder="Scan GTIN barcode..."
                                        disabled={scanning}
                                        autoComplete="off"
                                    />
                                </div>
                                <button className="oip-gtin-btn" onClick={handleGtinScan} disabled={!gtinInput.trim() || scanning}>
                                    {scanning
                                        ? <FontAwesomeIcon icon={faRotate} spin />
                                        : 'Scan'}
                                </button>
                            </div>
                        )}

                        {/* Feedback */}
                        {scanFeedback && (
                            <div className={`oip-feedback oip-feedback--${scanFeedback.type}`}>
                                <FontAwesomeIcon icon={feedbackIcon[scanFeedback.type]} className="oip-feedback-icon" />
                                {scanFeedback.message}
                            </div>
                        )}

                        {/* Items list */}
                        {assemblyLoading && (
                            <div className="oip-loading">
                                <FontAwesomeIcon icon={faRotate} spin /> Loading...
                            </div>
                        )}
                        {!assemblyLoading && assemblyItems.length === 0 && (
                            <div className="oip-empty">
                                <FontAwesomeIcon icon={faTriangleExclamation} className="oip-empty-icon" />
                                Order items not loaded.<br />Please sync this order with Veeqo first.
                            </div>
                        )}
                        {!assemblyLoading && assemblyItems.length > 0 && (
                            <ItemsTable
                                items={assemblyItems}
                                assemblyStatus={assemblyStatus}
                                itemStatus={itemStatus}
                                onManualScan={handleManualScan}
                                onUndoScan={handleUndoScan}
                                onAmazonShip={handleAmazonShip}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ItemsTable({ items, assemblyStatus, itemStatus, onManualScan, onUndoScan, onAmazonShip }) {
    return (
        <table className="oip-table oip-scan-table">
            <thead>
                <tr>
                    <th>SKU</th>
                    <th>Product</th>
                    <th>GTIN</th>
                    <th>Scanned</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {items.map((item, i) => {
                    const st = itemStatus(item);
                    const noGtin = !item.gtin;
                    return (
                        <tr key={i} className={`oip-scan-row oip-scan-row--${st}${item.is_amazon ? ' oip-scan-row--amazon' : ''}`}>
                            <td className="oip-sku">
                                {item.sku || '—'}
                                {item.is_amazon && <span className="oip-amazon-tag">Amazon</span>}
                            </td>
                            <td className="oip-product">{item.product_name || '—'}</td>
                            <td className="oip-gtin-cell">
                                {noGtin
                                    ? <span className="oip-no-gtin">
                                        <FontAwesomeIcon icon={faTriangleExclamation} /> no GTIN
                                      </span>
                                    : item.gtin}
                            </td>
                            <td className="oip-scan-count">
                                <span className={`oip-scan-badge oip-scan-badge--${st}`}>
                                    {st === 'done'
                                        ? <FontAwesomeIcon icon={faCheck} />
                                        : `${item.quantity_scanned}/${item.quantity}`}
                                </span>
                            </td>
                            <td className="oip-actions-cell">
                                {noGtin && st !== 'done' && assemblyStatus !== 'complete' && (
                                    <button
                                        className="oip-manual-btn"
                                        onClick={() => onManualScan(item.sku, null)}
                                        title="Mark as done manually"
                                    >
                                        <FontAwesomeIcon icon={faHandPointer} /> Done
                                    </button>
                                )}
                                {st !== 'done' && assemblyStatus !== 'complete' && (
                                    <button
                                        className="oip-amazon-btn"
                                        onClick={() => onAmazonShip(item.sku)}
                                        title="Mark as shipped via Amazon"
                                    >
                                        A
                                    </button>
                                )}
                                {item.quantity_scanned > 0 && assemblyStatus !== 'complete' && (
                                    <button
                                        className="oip-undo-btn"
                                        onClick={() => onUndoScan(item.sku)}
                                        title="Undo last scan"
                                    >
                                        <FontAwesomeIcon icon={faRotateLeft} />
                                    </button>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

export default OrderItemsPopup;
